import React from "react";
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  GridItem,
  GridItemProps,
  Text,
} from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useOrderHistoryProformaInvoice from "@/features/my-order/features/order-history/hooks/useOrderHistoryProformaInvoice";
import {
  extractInvoiceFromOrderHistory,
  extractUnoFeeFromOrderHistory,
} from "@/features/my-order/features/order-history/utils";
import { toCamelKeyObject } from "@/utils/case";
import OrderHistoryAccordion from "@/features/my-order/features/order-history/components/OrderHistoryAccordion";
import { RemittanceStatus } from "@/features/invoices/invoice.type";
import RemittanceStatusBadge from "@/features/my-order/components/RemittanceStatusBadge";
import { ProformaInvoice } from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";

function OrderHistoryInvoiceUnoFeeArticle() {
  const { tTitle, tCurrencyKrw, tSentence, isKor } = useI18nHelper();
  const { proformaInvoices, totalUnoFeeAmount } =
    useOrderHistoryProformaInvoice();
  const { calcNavigateTo } = useRouterHelper();
  const refinedProformaInvoices = toCamelKeyObject(proformaInvoices);
  const unoFeeSettlementMonth =
    refinedProformaInvoices?.at(0)?.unoFeeSettlementMonth;

  const unoFeeProformaInvoices =
    proformaInvoices?.filter((invoice) => {
      const hasUnoFeePayment = invoice.payments?.some(
        (payment) => payment.type === "UNO_FEE"
      );
      const { unoFeeAmount } = extractUnoFeeFromOrderHistory(
        toCamelKeyObject(invoice)
      );

      return hasUnoFeePayment && !!unoFeeAmount;
    }) ?? [];

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      <Flex justifyContent={"space-between"}>
        <Flex gap={"4px"}>
          <CustomIcon src={"coin"} width={24} height={24} color={"#8F8F8C"} />
          <Text textStyle={"body-lg-semi-bold"} color={"#444444"}>
            {`${tTitle("order_history:invoice.uno_fee")} (${
              isKor ? "총 " : ""
            }${tCurrencyKrw(totalUnoFeeAmount ?? 0)})`}
          </Text>
        </Flex>
        <Flex gap={"8px"} alignItems={"center"}>
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
            {tSentence("order_history:invoice.uno_fee_billing_description")}
          </Text>
          <Flex
            gap={"4px"}
            alignItems={"center"}
            className={"group"}
            cursor={"pointer"}
            onClick={() =>
              window.open(
                calcNavigateTo(
                  `/order-sheets/settlement?type=FEE&date=${
                    unoFeeSettlementMonth
                      ? new Date(unoFeeSettlementMonth)
                      : new Date()
                  }`
                ),
                "_blank"
              )
            }
          >
            <Text
              textStyle={"body-sm-bold"}
              _groupHover={{
                color: "#1272EF",
              }}
              _groupActive={{
                color: "#00417D",
              }}
              color={"#444444"}
            >
              {tTitle("order_history:invoice.show_bill")}
            </Text>
            <Box
              _groupHover={{
                color: "#1272EF",
              }}
              _groupActive={{
                color: "#00417D",
              }}
              color={"#8F8F8C"}
            >
              <CustomIcon src={"arrow-square"} width={16} height={16} />
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <Flex flexDirection={"column"} gap={"8px"}>
        {unoFeeProformaInvoices.length === 0 && (
          <OrderHistoryAccordion>
            <AccordionItem>
              <AccordionButton
                padding={0}
                cursor={"not-allowed"}
                _hover={{
                  background: "none",
                }}
              >
                <Flex justifyContent={"space-between"} width={"100%"}>
                  <Flex gap={"4px"}>
                    <CustomIcon
                      src={"caret-right"}
                      width={24}
                      height={24}
                      color={"#C0C0C0"}
                    />
                    <Text textStyle={"body-md-semi-bold"} color={"#363636"}>
                      {tTitle("common:commission")}
                    </Text>
                  </Flex>
                  <Text textStyle={"body-md-bold"} color={"#C0C0C0"}>
                    {tTitle("to_be_updated")}
                  </Text>
                </Flex>
              </AccordionButton>
            </AccordionItem>
          </OrderHistoryAccordion>
        )}
        {unoFeeProformaInvoices.map((invoice, index) => {
          return <OrderHistoryUnoFeeAccordion invoice={invoice} key={index} />;
        })}
      </Flex>
    </Flex>
  );
}

export default OrderHistoryInvoiceUnoFeeArticle;

interface OrderHistoryInvoiceUnoFeeAccordionProps {
  invoice: ProformaInvoice;
}

const OrderHistoryUnoFeeAccordion = ({
  invoice,
}: OrderHistoryInvoiceUnoFeeAccordionProps) => {
  const {
    unoFeeAmount,
    unoFeeSettlementMonth,
    isRemitted,
    status,
    unoFeeRate,
    currency,
    invoiceAmount,
    usedCredit,
    usedDeposit,
    earnedCredit,
    unoFeeBaseAmount,
    hasUnoFeeDeduction,
  } = extractUnoFeeFromOrderHistory(toCamelKeyObject(invoice));
  const { actualExchangeRate } = extractInvoiceFromOrderHistory(
    toCamelKeyObject(invoice)
  );
  const money = { type: "SYMBOL" as const, code: currency };
  const { tTitle, tCurrencyKrw, tCurrencyString, tSentence, isKor } =
    useI18nHelper();
  const [isOpen, setIsOpen] = React.useState(!isRemitted);

  // 우노 수수료(unoFeeAmount)는 부가세 10% 포함 금액이므로, 공급가액/부가세를 역산해 표시한다.
  const unoFeeSupplyAmount =
    unoFeeAmount != null ? Math.round(unoFeeAmount / 1.1) : null;
  const unoFeeVatAmount =
    unoFeeAmount != null && unoFeeSupplyAmount != null
      ? unoFeeAmount - unoFeeSupplyAmount
      : null;

  return (
    <OrderHistoryAccordion key={invoice.id} index={isOpen ? [0] : []}>
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <Flex
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={"8px"}
            >
              <AccordionButton
                justifyContent={"space-between"}
                alignItems={"center"}
                cursor={"default"}
                _hover={{
                  background: "none",
                }}
                padding={0}
                onClick={() => setIsOpen((prev) => !prev)}
              >
                <ToggleBar
                  isOpen={isExpanded}
                  name={invoice.name}
                  status={status}
                />
              </AccordionButton>
              <Text
                textStyle={"body-md-bold"}
                color={isRemitted ? "#444444" : "#CC1D1D"}
                whiteSpace={"nowrap"}
              >
                {tCurrencyKrw(unoFeeAmount ?? 0)}
              </Text>
            </Flex>
            <AccordionPanel padding={0}>
              <Box padding={"8px 1px 12px 28px"}>
                <Flex
                  padding={"16px"}
                  boxShadow={"0 0 0 0.5px #D4D4D1"}
                  borderRadius={"8px"}
                  flexDirection={"column"}
                  gap={"4px"}
                >
                  <Flex
                    justifyContent={"space-between"}
                    textStyle={"body-sm-medium"}
                    color={"#444444"}
                    paddingX={"8px"}
                  >
                    <Text>{tTitle("order_history:invoice.category")}</Text>
                    <Text>{tTitle("amount")}</Text>
                  </Flex>
                  <Flex
                    justifyContent={"space-between"}
                    paddingX={"8px"}
                    textStyle={"body-sm-regular"}
                  >
                    <Text color={"#6F6F6C"}>
                      {tTitle("order_history:invoice.invoice_amount")}
                    </Text>
                    <Text color={"#444444"}>
                      {tCurrencyString(invoiceAmount, money)}
                    </Text>
                  </Flex>
                  {/* 수수료 기준 금액 구성 — 백엔드 계산식 순서대로. 0 인 항목은 숨긴다. */}
                  {usedCredit !== 0 && (
                    <FeeRow
                      label={tTitle("order_history:invoice.used_credit")}
                      value={`- ${tCurrencyString(usedCredit, money)}`}
                    />
                  )}
                  {usedDeposit !== 0 && (
                    <FeeRow
                      label={tTitle("order_history:invoice.used_deposit")}
                      value={`- ${tCurrencyString(usedDeposit, money)}`}
                    />
                  )}
                  {earnedCredit !== 0 && (
                    <FeeRow
                      label={tTitle("order_history:invoice.earned_credit")}
                      value={`- ${tCurrencyString(earnedCredit, money)}`}
                    />
                  )}
                  {hasUnoFeeDeduction && (
                    <FeeRow
                      label={tTitle("order_history:invoice.uno_fee_base_amount")}
                      value={tCurrencyString(unoFeeBaseAmount, money)}
                      isEmphasized
                    />
                  )}
                  <Flex
                    justifyContent={"space-between"}
                    paddingX={"8px"}
                    textStyle={"body-sm-regular"}
                  >
                    <Text color={"#6F6F6C"}>{tTitle("exchange_rate")}</Text>
                    <Text color={"#444444"}>
                      ₩
                      {actualExchangeRate.toLocaleString("ko-KR", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })}
                    </Text>
                  </Flex>
                  <Flex
                    justifyContent={"space-between"}
                    paddingX={"8px"}
                    textStyle={"body-sm-regular"}
                  >
                    <Text color={"#6F6F6C"}>
                      {tTitle("settlement:fee_rate")}
                    </Text>
                    <Text color={"#444444"}>{unoFeeRate}%</Text>
                  </Flex>
                  <Flex
                    justifyContent={"space-between"}
                    paddingX={"8px"}
                    textStyle={"body-sm-regular"}
                  >
                    <Text color={"#6F6F6C"}>
                      {isKor ? "수수료 (공급가액)" : "Fee (excl. VAT)"}
                    </Text>
                    <Text color={"#444444"}>
                      {unoFeeSupplyAmount != null
                        ? "₩" + unoFeeSupplyAmount.toLocaleString()
                        : "-"}
                    </Text>
                  </Flex>
                  <Flex
                    justifyContent={"space-between"}
                    paddingX={"8px"}
                    textStyle={"body-sm-regular"}
                  >
                    <Text color={"#6F6F6C"}>
                      {isKor ? "부가세 (10%)" : "VAT (10%)"}
                    </Text>
                    <Text color={"#444444"}>
                      {unoFeeVatAmount != null
                        ? "₩" + unoFeeVatAmount.toLocaleString()
                        : "-"}
                    </Text>
                  </Flex>
                  <Flex
                    padding={"10px 8px"}
                    background={"#FFF7CC"}
                    boxShadow={"0 -0.5px 0 0 #D4D4D1"}
                    justifyContent={"space-between"}
                  >
                    <Text textStyle={"body-sm-medium"} color={"#444444"}>
                      {tTitle("settlement:total_billing_amount")}
                    </Text>
                    <Text
                      textStyle={"body-md-semi-bold"}
                      color={isRemitted ? "#1272EF" : "#CC1D1D"}
                    >
                      {unoFeeAmount ? "₩" + unoFeeAmount.toLocaleString() : "-"}
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </OrderHistoryAccordion>
  );
};

// 수수료 상세의 한 줄 — 왼쪽 항목명, 오른쪽 금액.
const FeeRow = ({
  label,
  value,
  isEmphasized = false,
}: {
  label: string;
  value: string;
  isEmphasized?: boolean;
}) => (
  <Flex
    justifyContent={"space-between"}
    paddingX={"8px"}
    textStyle={isEmphasized ? "body-sm-medium" : "body-sm-regular"}
  >
    <Text color={isEmphasized ? "#444444" : "#6F6F6C"}>{label}</Text>
    <Text color={"#444444"}>{value}</Text>
  </Flex>
);

interface TitleGridItemProps extends GridItemProps {
  children?: React.ReactNode;
}

const TitleGridItem = ({ children, ...props }: TitleGridItemProps) => {
  return (
    <GridItem textStyle={"body-sm-medium"} color={"#444444"} {...props}>
      {children}
    </GridItem>
  );
};

interface TogglerBarProps {
  isOpen: boolean;
  name: string;
  status?: RemittanceStatus;
}

const ToggleBar = ({ isOpen, name, status }: TogglerBarProps) => {
  return (
    <Flex
      alignItems={"center"}
      cursor={"pointer"}
      _hover={{
        background: "#F7F9FA",
      }}
      _active={{
        background: "#F0F0F0",
      }}
      paddingY={"4px"}
      flex={1}
    >
      <Flex alignItems={"center"}>
        <Box padding={"2px"}>
          <CustomIcon
            src={isOpen ? "caret-down" : "caret-right"}
            width={24}
            height={24}
            color={"#444444"}
          />
        </Box>
        <Flex alignItems={"center"} gap={"8px"}>
          <Text
            textStyle={"body-md-semi-bold"}
            color={"#363636"}
            noOfLines={1}
            maxWidth={"80%"}
            textAlign={"left"}
            wordBreak={"break-all"}
          >
            {name}
          </Text>
          <RemittanceStatusBadge status={status ?? "REMITTANCE_REQUIRED"} />
        </Flex>
      </Flex>
    </Flex>
  );
};
